// @ts-nocheck
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import {
  Zap,
  CheckCircle2,
  AlertTriangle,
  Layers,
  Sparkles,
  Search,
  RefreshCw,
  HelpCircle,
} from 'lucide-react';

interface BatchDecompositionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const BatchDecompositionModal: React.FC<BatchDecompositionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [filterTab, setFilterTab] = useState<'ALL' | 'READY_TO_SYNC' | 'NEEDS_SPEC' | 'SERVICE_BYPASS'>('READY_TO_SYNC');
  const [searchQuery, setSearchQuery] = useState('');
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [autoFillDemoSpecs, setAutoFillDemoSpecs] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isExecuting, setIsExecuting] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);

  // 1. Fetch Preview Data
  const { data: previewData, isLoading, refetch, isFetching } = useQuery(
    trpc.products.previewBatchDecomposition.queryOptions(
      {},
      {
        enabled: isOpen,
        staleTime: 30000,
      }
    )
  );

  // 2. Execute Batch Mutation
  const executeMutation = useMutation(
    trpc.products.executeBatchDecomposition.mutationOptions()
  );

  // Filtered Items
  const filteredItems = useMemo(() => {
    if (!previewData?.items) return [];
    return previewData.items.filter((item) => {
      // Tab filter
      if (filterTab !== 'ALL' && item.category !== filterTab) return false;
      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return item.productCode.toLowerCase().includes(q) || item.productName.toLowerCase().includes(q);
      }
      return true;
    });
  }, [previewData?.items, filterTab, searchQuery]);

  // Initial selection: Default to selecting all READY_TO_SYNC
  React.useEffect(() => {
    if (previewData?.items && selectedIds.size === 0) {
      const readyIds = new Set(
        previewData.items
          .filter((i) => i.category === 'READY_TO_SYNC')
          .map((i) => i.id)
      );
      setSelectedIds(readyIds);
    }
  }, [previewData?.items]);

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const toggleSelectAllFiltered = () => {
    const next = new Set(selectedIds);
    const allFilteredSelected = filteredItems.every((i) => next.has(i.id));
    if (allFilteredSelected) {
      filteredItems.forEach((i) => next.delete(i.id));
    } else {
      filteredItems.forEach((i) => {
        if (i.category !== 'SERVICE_BYPASS') next.add(i.id);
      });
    }
    setSelectedIds(next);
  };

  const handleExecute = async () => {
    const targetIds = Array.from(selectedIds);
    if (targetIds.length === 0) {
      toast.warning('Vui lòng chọn ít nhất một sản phẩm để chuẩn hóa!');
      return;
    }

    setIsExecuting(true);
    setProgressPercent(20);

    try {
      setProgressPercent(45);
      const res = await executeMutation.mutateAsync({
        productIds: targetIds,
        overwriteExisting,
        autoFillDemoSpecs,
        targetQuantity: 1000,
      });

      setProgressPercent(100);
      toast.success(
        `Chuẩn hóa thành công ${res.syncedCount}/${targetIds.length} sản phẩm! (+${res.totalBOMCreated} dòng BOM, +${res.totalRoutingCreated} dòng Routing)`,
        { duration: 5000 }
      );

      await refetch();
      if (onSuccess) onSuccess();
      setTimeout(() => {
        setIsExecuting(false);
        onClose();
      }, 1000);
    } catch (err: any) {
      setIsExecuting(false);
      toast.error(`Có lỗi xảy ra trong quá trình chuẩn hóa: ${err?.message || 'Lỗi không xác định'}`);
    }
  };

  const summary = previewData?.summary || {
    total: 0,
    readyCount: 0,
    needsSpecCount: 0,
    bypassCount: 0,
    alreadySyncedCount: 0,
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !isExecuting && !open && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] flex flex-col p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-slate-200 bg-slate-50/80">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-base md:text-lg font-bold text-slate-900 flex items-center gap-2">
                <span className="p-1.5 bg-amber-100 text-amber-700 rounded-lg">
                  <Zap className="h-5 w-5 fill-amber-500 text-amber-500" />
                </span>
                Bóc Tách Định Mức BOM Hàng Loạt
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 mt-1">
                Tự động bóc tách quy cách kỹ thuật, định mức vật tư giấy và công đoạn xưởng in vào Master Data
              </DialogDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading || isFetching || isExecuting}
              className="h-8 text-xs gap-1.5"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
              Làm mới
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* 4 KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="bg-slate-50/70 border-slate-200 shadow-none">
              <CardContent className="p-3">
                <div className="text-[11px] font-medium text-slate-500 flex items-center justify-between">
                  Tổng số sản phẩm
                  <Layers className="h-3.5 w-3.5 text-slate-400" />
                </div>
                <div className="text-xl font-bold text-slate-900 mt-1">
                  {isLoading ? '...' : summary.total}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Đã có Master: <span className="font-semibold text-emerald-600">{summary.alreadySyncedCount}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-emerald-50/60 border-emerald-200/80 shadow-none">
              <CardContent className="p-3">
                <div className="text-[11px] font-medium text-emerald-700 flex items-center justify-between">
                  Sẵn sàng chuẩn hóa
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                </div>
                <div className="text-xl font-bold text-emerald-700 mt-1">
                  {isLoading ? '...' : summary.readyCount}
                </div>
                <div className="text-[10px] text-emerald-600 mt-0.5">
                  Đủ vật tư & chuỗi công đoạn
                </div>
              </CardContent>
            </Card>

            <Card className="bg-amber-50/60 border-amber-200/80 shadow-none">
              <CardContent className="p-3">
                <div className="text-[11px] font-medium text-amber-700 flex items-center justify-between">
                  Cần bổ sung quy cách
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                </div>
                <div className="text-xl font-bold text-amber-700 mt-1">
                  {isLoading ? '...' : summary.needsSpecCount}
                </div>
                <div className="text-[10px] text-amber-600 mt-0.5">
                  Sản phẩm demo / thiếu vật tư
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-100/70 border-slate-200 shadow-none">
              <CardContent className="p-3">
                <div className="text-[11px] font-medium text-slate-600 flex items-center justify-between">
                  Dịch vụ / Miễn trừ
                  <HelpCircle className="h-3.5 w-3.5 text-slate-400" />
                </div>
                <div className="text-xl font-bold text-slate-700 mt-1">
                  {isLoading ? '...' : summary.bypassCount}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Phí vận chuyển, VAT, Chiết khấu
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Configuration Panel */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs space-y-2">
            <div className="font-semibold text-slate-800 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-indigo-600" />
              Tùy chọn cấu hình chuẩn hóa (Batch Options):
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox
                  checked={overwriteExisting}
                  onCheckedChange={(c) => setOverwriteExisting(!!c)}
                />
                <span className="text-slate-700">
                  <strong>Ghi đè dữ liệu cũ:</strong> Cập nhật lại toàn bộ BOM và Routing nếu sản phẩm đã có dữ liệu trước đó
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox
                  checked={autoFillDemoSpecs}
                  onCheckedChange={(c) => setAutoFillDemoSpecs(!!c)}
                />
                <span className="text-slate-700">
                  <strong>Tự động điền mẫu demo:</strong> Gán giấy tiêu chuẩn & công đoạn mặc định cho các mã demo thiếu quy cách
                </span>
              </label>
            </div>
          </div>

          {/* Search and Tabs */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1">
            <Tabs
              value={filterTab}
              onValueChange={(val: any) => setFilterTab(val)}
              className="w-full sm:w-auto"
            >
              <TabsList>
                <TabsTrigger value="ALL">
                  Tất cả ({summary.total})
                </TabsTrigger>
                <TabsTrigger value="READY_TO_SYNC">
                  Sẵn sàng ({summary.readyCount})
                </TabsTrigger>
                <TabsTrigger value="NEEDS_SPEC">
                  Cần bổ sung ({summary.needsSpecCount})
                </TabsTrigger>
                <TabsTrigger value="SERVICE_BYPASS">
                  Miễn trừ ({summary.bypassCount})
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" />
              <Input
                placeholder="Tìm mã hoặc tên sản phẩm..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-8 text-xs bg-white"
              />
            </div>
          </div>

          {/* Table Container */}
          <div className="border border-slate-200 rounded-lg overflow-x-auto max-h-[380px] overflow-y-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100/80 text-slate-700 font-semibold sticky top-0 z-10 border-b border-slate-200">
                <tr>
                  <th className="w-10 px-3 py-2 text-center">
                    <Checkbox
                      checked={
                        filteredItems.length > 0 &&
                        filteredItems.every((i) => selectedIds.has(i.id))
                      }
                      onCheckedChange={toggleSelectAllFiltered}
                      aria-label="Chọn tất cả"
                    />
                  </th>
                  <th className="px-3 py-2 w-28">Mã SP</th>
                  <th className="px-3 py-2">Tên sản phẩm</th>
                  <th className="px-3 py-2">Vật tư giấy bóc tách</th>
                  <th className="px-3 py-2 text-center w-24">Công đoạn</th>
                  <th className="px-3 py-2 text-right w-28">Giá vốn ước tính</th>
                  <th className="px-3 py-2 text-center w-36">Trạng thái</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-slate-500">
                      Đang phân tích cấu trúc sản phẩm...
                    </td>
                  </tr>
                ) : filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-slate-500">
                      Không có sản phẩm nào phù hợp với bộ lọc
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => {
                    const isSelected = selectedIds.has(item.id);
                    const isBypass = item.category === 'SERVICE_BYPASS';

                    return (
                      <tr
                        key={item.id}
                        onClick={() => !isBypass && toggleSelect(item.id)}
                        className={`hover:bg-slate-50/80 transition-colors ${
                          isSelected ? 'bg-indigo-50/40' : ''
                        } ${isBypass ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            disabled={isBypass}
                            onCheckedChange={() => toggleSelect(item.id)}
                          />
                        </td>
                        <td className="px-3 py-2 font-mono font-medium text-slate-800">
                          {item.productCode}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-800 break-words whitespace-normal max-w-xs">
                            {item.productName}
                          </div>
                          <div className="text-[10px] text-slate-400">ĐVT: {item.unit}</div>
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {item.materialName ? (
                            <span className="break-words whitespace-normal block max-w-xs font-mono text-[11px]">
                              {item.materialName}
                            </span>
                          ) : (
                            <span className="text-slate-400 italic">Chưa xác định</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {item.decomposedRoutingCount > 0 ? (
                            <Badge variant="secondary" className="text-[10px] font-normal">
                              {item.decomposedRoutingCount} công đoạn
                            </Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-medium text-slate-800">
                          {item.estimatedUnitCost > 0
                            ? `${item.estimatedUnitCost.toLocaleString()} đ`
                            : '-'}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {item.category === 'READY_TO_SYNC' ? (
                            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border-none text-[10px]">
                              Sẵn sàng
                            </Badge>
                          ) : item.category === 'NEEDS_SPEC' ? (
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200 border-none text-[10px]">
                              Thiếu quy cách
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-500 text-[10px]">
                              Miễn trừ
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Progress Bar during Execution */}
          {isExecuting && (
            <div className="p-3 bg-indigo-50/60 rounded-lg border border-indigo-100 space-y-2">
              <div className="flex items-center justify-between text-xs font-medium text-indigo-900">
                <span className="flex items-center gap-1.5">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin text-indigo-600" />
                  Đang chuẩn hóa và đồng bộ vào Master Data...
                </span>
                <span>{progressPercent}%</span>
              </div>
              <Progress value={progressPercent} className="h-2 bg-indigo-100" />
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            Đã chọn: <strong className="text-indigo-600">{selectedIds.size}</strong> / {summary.total} sản phẩm
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={isExecuting}
              className="h-8 text-xs"
            >
              Đóng
            </Button>
            <Button
              size="sm"
              onClick={handleExecute}
              disabled={isExecuting || selectedIds.size === 0}
              className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-sm gap-1.5"
            >
              <Zap className="h-3.5 w-3.5 fill-amber-300 text-amber-300" />
              Tiến Hành Chuẩn Hóa ({selectedIds.size} sản phẩm)
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
